import { useDispatch } from 'react-redux'
import { useTranslation } from 'react-i18next'
import {
  PROCESS_GROUP_ACTION_WITH_CONFIRMATION,
  UPDATE_ITEMS_BY_ID,
} from 'modules/Services/CampaignsEditor/types'
import NETWORKS_VK_ADS_V1 from 'api/networks/vk-ads/v1'
import styles from 'modules/Services/CampaignsEditor/campaigns-editor.module.scss'
import budgetEditorStyles from 'modules/Services/CampaignsEditor/subcomponents/BudgetEditor/budget-editor.module.scss'

export const useGroupActionConfig = (resource) => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const getActionConfig = (groupActionType, props = {}) => {
    switch (groupActionType) {
      case 'budget':
        return {
          className: styles['budget-editor-popover'],
          title: t('campaigns_editor::actions.budget.heading', {
            context: resource,
          }),
          startAction: 'MODAL/SHOW',
          modalType: 'budgetEditor',
          portalClassName: budgetEditorStyles['budget-editor-portal'],
          modalProps: {
            resource,
            shouldCloseOnOverlayClick: false,
            breakpoints: {
              0: {
                variant: 'fullViewport',
              },
              600: {
                variant: 'bottomSheet',
              },
              768: {
                variant: 'default',
              },
            },
          },
        }
      case 'activate':
        return {
          groupActionType,
          ...props,
          title: t('campaigns_editor::actions.confirm.activate.heading', {
            context: resource,
          }),
          text: t('campaigns_editor::actions.confirm.activate.text', {
            context: resource,
          }),
          buttons: {
            apply: t(
              'campaigns_editor::actions.confirm.activate.buttons.apply',
            ),
            cancel: t(
              'campaigns_editor::actions.confirm.activate.buttons.cancel',
            ),
          },
          API_CALL: NETWORKS_VK_ADS_V1.activate,
          preFilter: (rows) =>
            rows.filter((r) => ['blocked'].includes(r.status)),
          startAction: PROCESS_GROUP_ACTION_WITH_CONFIRMATION,
          postAction: {
            type: UPDATE_ITEMS_BY_ID,
            payload: {
              removeRowCondition: (filters) =>
                !!filters?.statuses.length &&
                !filters?.statuses?.includes('active'),
              updater: (row, update) => ({
                ...row,
                status: update.status === 'success' ? 'active' : row.status,
              }),
            },
          },
        }

      case 'delete':
        return {
          groupActionType,
          ...props,
          title: t('campaigns_editor::actions.confirm.delete.heading', {
            context: resource,
          }),
          text: t('campaigns_editor::actions.confirm.delete.text', {
            context: resource,
          }),
          buttons: {
            apply: t('campaigns_editor::actions.confirm.delete.buttons.apply'),
            cancel: t(
              'campaigns_editor::actions.confirm.delete.buttons.cancel',
            ),
            attention: true,
          },
          API_CALL: NETWORKS_VK_ADS_V1.delete,
          preFilter: (rows) =>
            rows.filter((r) => ['active', 'blocked'].includes(r.status)),
          startAction: PROCESS_GROUP_ACTION_WITH_CONFIRMATION,
          postAction: {
            type: UPDATE_ITEMS_BY_ID,
            payload: {
              removeRowCondition: (filters) =>
                !!filters?.statuses.length &&
                !filters?.statuses?.includes('deleted'),
              updater: (row, update) => ({
                ...row,
                status: update.status === 'success' ? 'deleted' : row.status,
              }),
            },
          },
        }

      case 'block':
        return {
          groupActionType,
          ...props,
          title: t('campaigns_editor::actions.confirm.block.heading', {
            context: resource,
          }),
          text: t('campaigns_editor::actions.confirm.block.text', {
            context: resource,
          }),
          buttons: {
            apply: t('campaigns_editor::actions.confirm.block.buttons.apply'),
            cancel: t('campaigns_editor::actions.confirm.block.buttons.cancel'),
          },
          API_CALL: NETWORKS_VK_ADS_V1.block,
          startAction: PROCESS_GROUP_ACTION_WITH_CONFIRMATION,
          preFilter: (rows) =>
            rows.filter((r) => ['active'].includes(r.status)),
          postAction: {
            type: UPDATE_ITEMS_BY_ID,
            payload: {
              removeRowCondition: (filters) =>
                !!filters?.statuses.length &&
                !filters?.statuses?.includes('blocked'),
              updater: (row, update) => ({
                ...row,
                status: update.status === 'success' ? 'blocked' : row.status,
              }),
            },
          },
        }

      case 'restore':
        return {
          groupActionType,
          ...props,
          title: t('campaigns_editor::actions.confirm.restore.heading', {
            context: resource,
          }),
          text: t('campaigns_editor::actions.confirm.restore.text', {
            context: resource,
          }),
          buttons: {
            apply: t('campaigns_editor::actions.confirm.restore.buttons.apply'),
            cancel: t(
              'campaigns_editor::actions.confirm.restore.buttons.cancel',
            ),
          },
          API_CALL: NETWORKS_VK_ADS_V1.block,
          preFilter: (rows) => rows.filter((r) => r.status === 'deleted'),
          startAction: PROCESS_GROUP_ACTION_WITH_CONFIRMATION,
          postAction: {
            type: UPDATE_ITEMS_BY_ID,
            payload: {
              removeRowCondition: (filters) =>
                !!filters?.statuses.length &&
                !filters?.statuses?.includes('delete'),
              updater: (row, update) => ({
                ...row,
                status: update.status === 'success' ? 'blocked' : row.status,
              }),
            },
          },
        }
    }
  }

  const runGroupAction = async ({ groupActionConfig, ids }) => {
    try {
      const result = await new Promise((resolve, reject) =>
        dispatch({
          type: groupActionConfig.startAction,
          payload: { ...groupActionConfig, ids, meta: { resolve, reject } },
          meta: { resolve, reject },
        }),
      )

      if (groupActionConfig.postAction && result.data) {
        dispatch({
          type: groupActionConfig.postAction.type,
          payload: {
            groupActionConfig,
            ...(groupActionConfig.postAction.payload ?? {}),
            data: result.data,
            ids,
          },
        })
      }

      return result
    } catch (err) {
      console.error('Error in group action', err)
      throw err
    }
  }

  return [getActionConfig, runGroupAction]
}
